/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { LicenseKey } from 'app/common/constants/license-key';
import { ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';

import { type DataConfig, DataTypeE, GlobalSearchStrService } from '../../global-search-str.service';
import { GlobalSearchConfigService } from '../global-search-config.service';

@Injectable({ providedIn: 'root' })
export class ConfigSystemStrService {
    systemDataMap: { [key: string]: DataConfig };

    constructor(
        private configService: GlobalSearchConfigService,
        private str: GlobalSearchStrService,
    ) { }

    /**
     * 返回dataMap
     */
    getDataMap(): { [key: string]: DataConfig } {
        this.systemDataMap = {
            /**
             * 硬件总览-资产清单
             */
            [DataTypeE.hardware]: {
                type: DataTypeE.hardware,
                url: '/system/hardware',
                tab: this.str.i18n.resourceListTab,
                page: {
                    searchKey: 'system.hardware_term_resourceList_tab',
                    permission: [
                        // 微存储不支持机柜&级联版【不需要该权限的依赖】
                        ...(FeatureConfig.IsHideCabinetAndExpansion ? [] : [
                            'cabinet_R',
                            'expansion_module_R',
                        ]),
                        'enclosure_R',
                        'controller_R',
                        'interface_module_R',
                        'fan_R',
                        'power_supply_R',
                        'bbu_R',
                        'disk_R',
                        'port_R',
                    ],
                    defaultSearch: true,
                },
            },
            /**
             * 设备
             */
            [DataTypeE.device]: {
                type: DataTypeE.device,
                url: '/system/hardware',
                tab: this.str.i18n.deviceTab,
                page: {
                    searchKey: 'system.Devices_tab',
                    permission: [
                        // 微存储不支持机柜&级联版【不需要该权限的依赖】
                        ...(FeatureConfig.IsHideCabinetAndExpansion ? [] : [
                            'cabinet_R',
                            'expansion_module_R',
                        ]),
                        'enclosure_R',
                        'controller_R',
                        'interface_module_R',
                        'fan_R',
                        'power_supply_R',
                        'bbu_R',
                        'disk_R',
                        'port_R',
                    ],
                },
            },

            /**
             * 外部存储
             */
            // 计算型存储屏蔽
            [DataTypeE.externalStorage]: {
                type: DataTypeE.externalStorage,
                url: '/system/external-storage',
                license: FeatureConfig.IsNeedPermissionAndLicenseBeforeShowExternalStorage ?
                    [LicenseKey.SmartVirtualization, LicenseKey.SmartThin] :
                    [LicenseKey.SmartThin],
                isNotSupportFeatureInOceanMicro: FeatureConfig.IsHideExternalStorage,
                operation: [
                    {
                        searchKey: 'protection.externalStorage_task_addFCExternalDevice_tittle',
                        action: 'searchScanFcDevice',
                        permission: ['remote_device_R', 'remote_device_W', 'target_W'],
                        // 计算型存储屏蔽FC端口
                        noFcFeature: !FeatureConfig.IsSupportFC,
                    },
                    {
                        searchKey: 'home.search_term_addIscsiExterDevice_lab',
                        action: 'searchAddIscsiDevice',
                        permission: ['remote_device_R', 'remote_device_W', 'target_W'],
                    },
                ],
                page: {
                    searchKey: 'common.externalStorage_title',
                    permission: ['remote_device_R'],
                },
            },

            /**
             * 存储池
             */
            [DataTypeE.storagePool]: {
                type: DataTypeE.storagePool,
                url: '/system/storage-pool',
                tab: this.str.i18n.storagePoolTitle,
                isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
                operation: [
                    {
                        searchKey: 'home.InitialConfig_createStoragePool_lab',
                        action: 'searchCreate',
                        permission: ['storage_pool_R', 'disk_domain_R', 'storage_pool_W'],
                        // 池化可得容量模式屏蔽创建存储池
                        noCreateStoragePoolFeature: SWITCH.IS_AvailableCap_MODE || !FeatureConfig.IsEnableStoragePoolCreation,
                    },
                ],
                page: {
                    searchKey: 'common.storagePool_title',
                    permission: ['storage_pool_R', 'disk_domain_R'],
                },
            },

            /**
             * 网络平面
             */
            [DataTypeE.netwrkPlane]: {
                type: DataTypeE.netwrkPlane,
                url: 'system/smart-container/network-planes',
                page: {
                    searchKey: 'system.container_networkPlane_task_title',
                    permission: [FeatureConfig.IsSupportVM ? 'vm_service_R' : 'container_service_R', 'port_R', 'task_R', 'controller_R', 'lun_R', 'upgrade_R'],
                    license: [
                        [
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                            LicenseKey.VirtualEnvironmentEnabler,
                        ],
                    ],
                },
            },
            [DataTypeE.application]: {
                type: DataTypeE.application,
                url: 'system/smart-container/applications',
                page: {
                    searchKey: 'system.container_app_task_title',
                    permission: ['container_service_R', 'task_R', 'controller_R', 'lun_R', 'upgrade_R', 'port_R', 'container_application_R'],
                    license: [
                        [
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                        ],
                    ],
                },
                hide: !ShardSwitch.CONTAINER_REPOSITORY_AVAILABLE,
            },
            /**
             * Chart
             */
            ...this.getChartDataMap(),
            /**
             * 虚拟机
             */
            ...this.getVirtualMachineDataMap(),
            /**
             * Image
             */
            ...this.getImageDataMap(),
            /**
             * 性能层
             */
            ...(!FeatureConfig.IsSupportSmartPool ? {} : {
                [DataTypeE.performanceLayer]: {
                    type: DataTypeE.performanceLayer,
                    url: '/system/storage-pool',
                    tab: this.str.i18n.performanceLayersTab,
                    page: {
                        searchKey: 'storage.storagePool_create_task_performanceLayers_tab',
                        permission: ['storage_pool_R', 'disk_domain_R'],
                    },
                },
            }),
        };

        return this.systemDataMap;
    }

    /**
     * 返回镜像 dataMap
     */
    getImageDataMap(): { [key: string]: DataConfig } {
        return {
            [DataTypeE.image]: {
                type: DataTypeE.image,
                url: 'system/smart-container/repository',
                tab: this.str.i18n.imagesTab,
                page: {
                    searchKey: 'system.container_repository_task_images_tab',
                    permission: [FeatureConfig.IsSupportVM ? 'vm_service_R' : 'container_service_R', 'task_R', 'controller_R', 'lun_R', 'upgrade_R'],
                    license: [
                        [
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                            LicenseKey.VirtualEnvironmentEnabler,
                        ],
                    ],
                },
                hide: !ShardSwitch.CONTAINER_REPOSITORY_AVAILABLE,
            },
        };
    }

    /**
     * 返回虚拟机 dataMap
     */
    getVirtualMachineDataMap(): { [key: string]: DataConfig } {
        return {
            [DataTypeE.VIRTUAL_MACHINE]: {
                type: DataTypeE.VIRTUAL_MACHINE,
                url: 'system/smart-container/virtual-machine',
                tab: this.str.i18n.imagesTab,
                page: {
                    searchKey: 'system.vm_app_task_title',
                    permission: ['vm_instance_R', 'port_R'],
                    license: [
                        [
                            LicenseKey.VirtualEnvironmentEnabler,
                        ],
                    ],
                },
            },
        };
    }

    /**
     * 返回Chart dataMap
     */
    getChartDataMap(): { [key: string]: DataConfig } {
        return {
            [DataTypeE.chart]: {
                type: DataTypeE.chart,
                url: 'system/smart-container/repository',
                tab: this.str.i18n.chartsTab,
                page: {
                    searchKey: 'system.container_repository_task_charts_tab',
                    permission: ['container_service_R', 'task_R', 'controller_R', 'lun_R', 'upgrade_R'],
                    license: [
                        [
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                        ],
                    ],
                },
                hide: !ShardSwitch.CONTAINER_REPOSITORY_AVAILABLE,
            },
        };
    }
}
